/**
 * Formatação de data, moeda e telefone, IDÊNTICA à do admin atual. Todas as telas usam daqui.
 *
 * Cada função é o porte literal da original, com o `admin.html:linha` ao lado. Migração reproduz o
 * funcionamento: o mesmo valor de entrada produz o mesmo texto na tela, inclusive nos casos de borda
 * (valor não numérico vira "R$ 0,00", telefone fora de 10 ou 11 dígitos volta como veio).
 *
 * Esta pasta (`comum/`) é do principal. Precisou de outra peça comum? Peça a ele, em vez de criar uma
 * cópia na pasta da sua tela.
 */

// Data no formato AAAA-MM-DD, pela hora LOCAL (`admin.html:4352`). É o que os campos `date` e as
// consultas por dia usam; `toISOString` daria o dia em UTC, e à noite no Brasil isso já é amanhã.
export function formatarDataYMD(data: Date): string {
  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
}

// Data e hora para nome de arquivo, AAAA-MM-DD_HHMM (`admin.html:5497`).
export function formatarDataHoraArquivo(data: Date): string {
  // Dois dígitos, com zero à esquerda.
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${data.getFullYear()}-${pad(data.getMonth() + 1)}-${pad(data.getDate())}_${pad(data.getHours())}${pad(data.getMinutes())}`;
}

// "Último acesso: DD/MM/AAAA às HH:MM" (`admin.html:5923`). Sem data, texto vazio, como no original.
export function formatarUltimoAcesso(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `Último acesso: ${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

// Telefone com máscara: 11 dígitos (celular) ou 10 (fixo); qualquer outro volta como veio
// (`admin.html:7700`).
export function formatarTelefone(tel: string | null | undefined): string {
  if (!tel) return '';
  // Só os dígitos, para a máscara não depender de como o número foi digitado.
  const d = tel.replace(/\D/g, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return tel;
}

/**
 * A MÁSCARA ENQUANTO SE DIGITA, que é outra coisa da de cima e por isso é outra função.
 *
 * `formatarTelefone` só desenha o número COMPLETO (10 ou 11 dígitos) e devolve o resto como veio.
 * Isso serve para mostrar um telefone guardado, e não para acompanhar quem está digitando: o
 * fundador apontou em 07/10/2026, na inclusão de cliente, o campo com `619872787272` cru.
 *
 * Esta aqui formata o que já foi digitado, dígito a dígito, e CORTA em 11, que é o máximo de um
 * celular brasileiro com DDD. Cortar é o que impede o `619872787272` de existir: sem limite, o
 * campo aceita qualquer coisa e a máscara desiste.
 */
export function mascararTelefoneDigitando(valor: string): string {
  // Só os dígitos, e no máximo 11: o que passar disso é digitação errada, e não número.
  const d = valor.replace(/\D/g, '').slice(0, 11);
  // Campo vazio continua vazio: devolver "(" faria o campo nascer com lixo dentro.
  if (d.length === 0) return '';
  // Só o DDD, ainda sem fechar o parêntese: fechar cedo empurraria o cursor para fora.
  if (d.length <= 2) return `(${d}`;
  // O corte do prefixo muda com o tamanho: 8 dígitos no fixo, 9 no celular.
  const corte = d.length <= 10 ? 6 : 7;
  if (d.length <= corte) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, corte)}-${d.slice(corte)}`;
}

// Moeda em reais, R$ 1.234,56 (`admin.html:7818`). Valor que não é número vira "R$ 0,00", como no
// original, e não "R$ NaN".
export function formatarMoeda(valor: number): string {
  if (isNaN(valor)) return 'R$ 0,00';
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
